<template>
  <div class="customer-boundary-editor">
    <!--NOTE: Similar UI in PaidCreatorFields-->
    <div class="customer-boundary-editor__modes">
      <label class="customer-boundary-editor__mode" :class="{ 'customer-boundary-editor__mode--active': boundarySelectMode === 'draw' }" @click="setBoundaryMode('draw')">
        <input type="radio" name="customer-boundary-mode" :checked="boundarySelectMode === 'draw'" style="width: 13px; margin-right: 8px" />
        <i class="pencil alternate icon"></i> Draw Boundary On Map
      </label>
      <label class="customer-boundary-editor__mode" :class="{ 'customer-boundary-editor__mode--active': boundarySelectMode === 'radius' }" @click="setBoundaryMode('radius')">
        <input type="radio" name="customer-boundary-mode" :checked="boundarySelectMode === 'radius'" style="width: 13px; margin-right: 8px" />
        <i class="map marker alternate icon"></i> Radius around your Destination
      </label>
      <h3 v-if="props.validationMessage" class="customer-boundary-editor__validation">{{ props.validationMessage }}</h3>
    </div>
    <div class="customer-boundary-editor__map-top" v-if="boundarySelectMode">
      <div>
        <div v-if="boundarySelectMode === 'radius'">
          Everything within
          <input type="number" min="10" max="400" step="10" v-model="withinMiles" style="width: 70px; height: 25px; display: inline; margin-left: 0.5rem; margin-right: 0.5rem" />
          miles of the map marker is included.
        </div>
        <h4 v-else-if="boundarySelectMode === 'draw'">Click on the map to create your boundary!</h4>
      </div>
      <div style="display: flex; align-items: center">
        <h4 v-if="boundarySelectMode === 'radius'">The map marker is draggable</h4>
      </div>
    </div>
    <DrawingMap
      style="width: 100%; margin: 0px"
      mapAspectRatio="2/1"
      :lat="props.latitude"
      :lng="props.longitude"
      :inputGeoJson="boundaryGeoJson"
      :circleRadiusInMiles="+(withinMiles ?? 0)"
      :boundarySelectMode="boundarySelectMode"
      :onlyAllowDrawingMode="false"
      :isCreatorFlow="false"
      @boundaryChanged="boundaryChanged"
      @markerManuallyMoved="(latitude, longitude) => emit('markerMoved', latitude, longitude)"
    />
  </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { buildSquareAroundCenterAsGeoJson } from "@helpers/GeoSpatialHelper";

// Components
import DrawingMap from "./DrawingMap.vue";

// Types
import type { BoundarySelectMode } from "@contracts/pages";

// A destination's boundary: a shape drawn on the map, or a radius around the map marker.
// Used on Organization Settings (Creator Visits tab) and the superuser Create Customer page.
// One v-model per field, so a change to one field never overwrites another made in the same tick.
const boundarySelectMode = defineModel<BoundarySelectMode | null>("boundarySelectMode");
const withinMiles = defineModel<number | null>("withinMiles");
const boundaryGeoJson = defineModel<string | null>("boundaryGeoJson");
const boundaryAreaInMiles = defineModel<number | null>("boundaryAreaInMiles");

const props = withDefaults(
  defineProps<{
    /** The map marker (the destination's location). */
    latitude: number | null;
    longitude: number | null;
    /** Shown under the mode choices, ex. "Click the map to draw your boundary". */
    validationMessage?: string | null;
  }>(),
  { validationMessage: null }
);

const emit = defineEmits<{
  (e: "markerMoved", latitude: number, longitude: number): void;
  (e: "modeChanged", mode: BoundarySelectMode): void;
}>();

const globalRemoteLogger = inject("globalRemoteLogger") as any;

function setBoundaryMode(mode: BoundarySelectMode) {
  boundarySelectMode.value = mode;

  // Default the bounding box if they select draw mode
  // Note: We have similar logic on the creator side in onSelectBoundaryMode
  if (mode === "draw" && !boundaryGeoJson.value && props.latitude && props.longitude) {
    const geoJson = buildSquareAroundCenterAsGeoJson(props.latitude, props.longitude, withinMiles.value ?? 10);
    boundaryGeoJson.value = geoJson;
    globalRemoteLogger?.info(`Destination: Defaulting square boundary with centerLat=${props.latitude}, centerLng=${props.longitude}: ${geoJson}`);
  }
  emit("modeChanged", mode);
}

function boundaryChanged(geoJsonBoundaryString: string | null, areaInMiles: number) {
  boundaryGeoJson.value = geoJsonBoundaryString;
  boundaryAreaInMiles.value = areaInMiles;
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Customer boundary editor ===================================================
.customer-boundary-editor {
  &__modes {
    margin-top: 1rem;
  }

  &__mode {
    width: auto !important;
    padding: 12px 16px;
    margin-right: 1rem !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 4px;
    display: inline-flex !important;
    background-color: #eaeaea !important;
    cursor: pointer;

    label {
      cursor: pointer;
    }

    &--active {
      background-color: $brand-color-lighter !important;
      border-color: $brand-color-light !important;
      label {
        color: $brand-color !important;
      }
    }
  }

  &__validation {
    color: red;
    font-weight: bold;
    margin-top: 10px;
  }

  &__map-top {
    padding: 8px 0;
    display: flex;
    justify-content: space-between;
  }
}
</style>
